import { API_BASE_URL } from '../config/serverApiConfig';
import { api } from './api';

/**
 * Access invitations. There is one system: an organisation invites people onto nodes of its
 * own tree (`/r/v2/organisations/{id}/invitations`), the platform invites a support admin
 * (`/platform/support-admins/invitations`, see platform.service), and the invitee accepts or
 * declines under `/invitations`. A workspace does not invite; it can only add somebody the
 * organisation already has (workspaceMembers.service).
 *
 * Reads are uncached: `api()` installs axios-cache-interceptor, and an inbox read straight
 * after an accept or a decline must not show the invitation that was just answered.
 */
const fresh = { cache: false } as any;

export interface InvitationGrant {
  node_id: number;
  node_name: string;
  role: string | null;
  custom_role_id: number | null;
  custom_role_name: string | null;
}

/** One row of the invitee's inbox, and of an organisation's invitation list. */
export interface InvitationRow {
  id: number;
  email: string;
  name: string | null;
  status: string;
  organisation_id: number | null;
  organisation_name: string | null;
  /** `SUPPORT_ADMIN` on a platform invitation, `null` on an organisation's. */
  platform_role: string | null;
  expires_at: string;
  created_at: string;
  grants: InvitationGrant[];
}

/** What the holder of an invitation link may see before signing in. */
export interface InvitationDetails {
  email: string;
  organisation_name: string | null;
  platform_role: string | null;
  grants: InvitationGrant[];
  expires_at: string;
  /**
   * How a signed-out invitee gets a session: `temporary_password` (an organisation invitation's
   * account was made with a temporary password mailed to the invitee), `set_password` (a platform
   * invitation's account was provisioned and mailed a set-up link), `register` (self-registration is open) or `sign_in`. Absent from
   * an older backend.
   */
  account_setup?: 'temporary_password' | 'set_password' | 'register' | 'sign_in';
}

/** The outcome of accepting: an organisation's grants, or the platform role. */
export interface AcceptedInvitation {
  status: 'accepted';
  organisation_id?: number;
  platform_role?: string;
}

/**
 * The membership an organisation invitation requests: a node, and exactly one of a built-in
 * `role` or a `custom_role_id`.
 */
export interface OrganisationGrantBody {
  node_id: number;
  role?: string;
  custom_role_id?: number;
}

export interface OrganisationInviteBody {
  email: string;
  name?: string;
  grants: OrganisationGrantBody[];
}

/**
 * `granted`: the address already belonged to the organisation, so the grants were applied at
 * once. `invitation_pending`: a link was issued; `delivery` says whether the mail went.
 */
export interface OrganisationInviteResult {
  status: 'granted' | 'invitation_pending';
  user_id?: string;
  invitation_id?: number;
  delivery?: 'sent' | 'failed';
  expires_at?: string;
}

/** A workspace the caller reaches, trimmed to what the organisation screens read. */
export interface WorkspaceNode {
  id: number;
  name: string;
  parent_id: number | null;
  is_organisation: boolean;
}

// -- the invitee -------------------------------------------------------------------------

/** Public: the invitee may have no account yet. A bad token is 404, a used one 409, a lapsed one 410. */
export const getInvitation = (token: string): Promise<InvitationDetails> =>
  api()
    .get(`${API_BASE_URL}invitations/${encodeURIComponent(token)}`, fresh)
    .then((r) => r.data);

/** Public: mail the provisioned account's set-up link again. Limited to 3 an hour (429). */
export const requestAccountSetupEmail = (token: string): Promise<{ status: 'sent' }> =>
  api()
    .post(`${API_BASE_URL}invitations/${encodeURIComponent(token)}/setup-email`)
    .then((r) => r.data);

export const acceptInvitation = (token: string): Promise<AcceptedInvitation> =>
  api()
    .post(`${API_BASE_URL}invitations/${encodeURIComponent(token)}/accept`)
    .then((r) => r.data);

export const declineInvitation = (token: string): Promise<{ status: 'declined' }> =>
  api()
    .post(`${API_BASE_URL}invitations/${encodeURIComponent(token)}/decline`)
    .then((r) => r.data);

export const getMyInvitations = (): Promise<InvitationRow[]> =>
  api()
    .get(`${API_BASE_URL}invitations/me`, fresh)
    .then((r) => r.data?.invitations ?? []);

export const acceptMyInvitation = (id: number): Promise<AcceptedInvitation> =>
  api()
    .post(`${API_BASE_URL}invitations/me/${id}/accept`)
    .then((r) => r.data);

export const declineMyInvitation = (id: number): Promise<{ status: 'declined' }> =>
  api()
    .post(`${API_BASE_URL}invitations/me/${id}/decline`)
    .then((r) => r.data);

// -- the organisation --------------------------------------------------------------------

const organisationBase = (organisationId: number) =>
  `${API_BASE_URL}r/v2/organisations/${organisationId}/invitations`;

export const listOrganisationInvitations = (organisationId: number): Promise<InvitationRow[]> =>
  api()
    .get(organisationBase(organisationId), fresh)
    .then((r) => r.data?.invitations ?? []);

export const inviteToOrganisation = (
  organisationId: number,
  body: OrganisationInviteBody
): Promise<OrganisationInviteResult> =>
  api()
    .post(organisationBase(organisationId), body)
    .then((r) => r.data);

export const resendOrganisationInvitation = (
  organisationId: number,
  invitationId: number
): Promise<OrganisationInviteResult> =>
  api()
    .post(`${organisationBase(organisationId)}/${invitationId}/resend`)
    .then((r) => r.data);

export const revokeOrganisationInvitation = (organisationId: number, invitationId: number) =>
  api()
    .post(`${organisationBase(organisationId)}/${invitationId}/revoke`)
    .then((r) => r.data);

/**
 * Every workspace the caller reaches, read raw. The shared `getWorkspaceList` hands a failed
 * read back as an error object that is easy to mistake for a list; here a failure throws, so
 * the caller can tell "no organisation" from "could not read".
 */
export const listWorkspaceNodes = async (): Promise<WorkspaceNode[]> => {
  const response = await api().get(`${API_BASE_URL}r/v2/workspaces`, fresh);
  const rows: any[] = Array.isArray(response.data) ? response.data : (response.data?.workspaces ?? []);
  return rows.map((row) => ({
    id: row.id,
    name: row.name,
    parent_id: row.parent_id ?? null,
    is_organisation: row.is_organisation === true
  }));
};

/**
 * The organisation a workspace belongs to, found by walking `parent_id` up the rows the
 * caller reaches. Only a row that is itself an organisation counts: a caller who reaches a
 * workspace but not the organisation above it has no organisation to administer here, and
 * the capability read on a guessed parent id would only answer 404.
 */
export const findOrganisationRoot = (
  nodes: WorkspaceNode[],
  workspaceId: number | undefined
): WorkspaceNode | undefined => {
  if (workspaceId == null) return undefined;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  let current = byId.get(workspaceId);
  for (let hops = 0; current && hops < nodes.length; hops += 1) {
    if (current.is_organisation) return current;
    current = current.parent_id == null ? undefined : byId.get(current.parent_id);
  }
  return undefined;
};

/** The nodes an organisation invitation may grant: the organisation itself and everything beneath it. */
export const nodesOfOrganisation = (nodes: WorkspaceNode[], rootId: number): WorkspaceNode[] => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const belongs = (node: WorkspaceNode): boolean => {
    let current: WorkspaceNode | undefined = node;
    for (let hops = 0; current && hops <= nodes.length; hops += 1) {
      if (current.id === rootId) return true;
      current = current.parent_id == null ? undefined : byId.get(current.parent_id);
    }
    return false;
  };
  return nodes.filter(belongs);
};
